import { Separator } from "@/components/ui/separator";
import { Dot, EllipsisVertical, User, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { toast } from "react-hot-toast";
import useAuth from "@/hooks/useAuth";
import { createBookingTable, createRoom, createTable, getAllRooms, getAllRoomsWithoutPagination, getAllTablesWithRoom, mergeTable, quickBookingTable, updateMergeTable, updateRoom, updateTable } from "@/api/table-floor/tableFloor";
import { useSelector, useDispatch } from "react-redux";
import { fetchAllTables } from "@/redux/slice/table-slice/table-slice";

const addNewFloorCardSchema = z.object({
  floor_id: z.number().min(1, "Floor is required"),
  room_id: z.number().min(1, "Room is required"),
  table_ids: z.array(z.number()).min(2, "Minimum 2 tables required"),
  // merge_date: z.preprocess((val) => {
  //   if (!val) return undefined;
  //   const date = new Date(val);
  //   return date.toISOString().split("T")[0];
  // }, z.string({ required_error: "End date is required" })),
  merge_date: z.preprocess((val) => {
    if (!val) return undefined;
    const date = new Date(val);
    return date.toISOString().split("T")[0];
  }, z.string().optional()),
  merge_time: z.string().optional(),
});

const MergeTableModal = ({ isOpen, onClose, itemsDialog, formType }) => {

  const { floors, selectedFloor, selectedRoom, tables } = useSelector(
    (state) => state.table.table
  );
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [rooms, setRooms] = useState([])
  // const [tables, setTables] = useState([])
  
  const dispatch = useDispatch();  

  const form = useForm({
    resolver: zodResolver(addNewFloorCardSchema),
    mode: "onChange",
    defaultValues: {
      floor_id: "",
      room_id: "",
      table_ids: ["", ""],
      merge_date: "",
      merge_time: "",
    },
  });
  // useEffect(() => {
  //   if (formType === "update" && itemsDialog) {
  //     form.reset({
  //       floor_id: itemsDialog.floor_id,
  //       room_id: itemsDialog.room_id,
  //       merge_date: itemsDialog.merge_date,
  //       merge_time: itemsDialog.merge_time.slice(0, 5), // "04:48:00" → "04:48"
  //       table_ids: itemsDialog.tables.map(t => t.id),
  //     });
  //   }
  // }, [formType, itemsDialog]);
  useEffect(() => {
    if (formType === "update" && itemsDialog) {
      form.reset({
        floor_id: itemsDialog.floor_id,
        room_id: itemsDialog.room_id,
        merge_date: itemsDialog.merge_date,
        merge_time: itemsDialog.merge_time.slice(0, 5),
        table_ids: normalizeTableIds(itemsDialog.tables.map(t => t.id)),
      });
    }

    if (formType === "create") {
      form.reset({
        floor_id: "",
        room_id: "",
        table_ids: ["", ""], // default 2
        merge_date: "",
        merge_time: "",
      });
    }
  }, [formType, itemsDialog]);

  const floorId = form.watch("floor_id");
  const [roomReset, setRoomReset] = useState(false)
  useEffect(() => {
    if(floorId){
      getAllRoomsWithoutPagination(user?.restaurant_id, floorId)
        .then((res) => {
          if (res.status === "success") {
            if(formType !== "update") {
              form.setValue("room_id", "");
            }
            if(formType === "update" && roomReset) {
              form.setValue("room_id", "");
            }
            setRooms(res.data.rooms);
          }
        })
        .catch((err) => console.log(err));
    }
  }, [floorId]);

  const roomId = form.watch("room_id");
  useEffect(() => {
    if(roomId){
      // getAllTablesWithRoom(roomId)
      //   .then((res) => {
      //     if (res.status === "success") {
      //       setTables(res.data.tables);
      //     }
      //   })
      //   .catch((err) => console.log(err));
      dispatch(fetchAllTables(Number(roomId)));
    }
  }, [roomId]);

  const { reset } = form;

  const addFloorCardData = async (data) => {
    setLoading(true);

    const payload = {
      ...data,
      merge_time: data.merge_time.slice(0, 5), // ✅ ensure HH:MM
      restaurant_id: user.restaurant_id,
    };

    try {
      let res;

      if (formType === "update") {
        res = await updateMergeTable(itemsDialog.id, payload);
      } else {
        res = await mergeTable(payload);
      }

      if (res.status === "success") {
        toast.success(
          formType === "update"
            ? "Merge Table updated successfully!"
            : "Merge Table created successfully!"
        );

        onClose();
        reset();

        const roomId = data.room_id || selectedRoom?.id;
        if (roomId) {
          dispatch(fetchAllTables(Number(roomId)));
        }
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process merge table");
    } finally {
      setLoading(false);
    }
  };



  const tableIds = form.watch("table_ids");

  const MIN_TABLE = 2;
  const MAX_TABLE = tables?.length || 0;

  const addTable = () => {
    if (tableIds.length < MAX_TABLE) {
      form.setValue("table_ids", [...tableIds, ""]);
    }
  };

  const removeTable = (index) => {
    if (tableIds.length > MIN_TABLE) {
      const updated = tableIds.filter((_, i) => i !== index);
      form.setValue("table_ids", updated);
    }
  };


  const updateTable = (value, index) => {
    const updated = [...tableIds];
    updated[index] = Number(value);
    form.setValue("table_ids", updated);
  };


  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
        <Card className="bg-cblack4 text-cwhite w-[550px] overflow-scroll">
          <CardHeader className="flex flex-row justify-between items-center py-4 px-6">
            <CardTitle className="text-cwhite text-xl font-medium">Merge Table</CardTitle>
            <X onClick={()=>{onClose(); setRoomReset(false)}} className="cursor-pointer" size={16} />
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-4">
              <Form {...form}>
                {formType === 'view' ?
                  <div className="flex flex-col gap-3">
                    <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Floor</label>
                        <input
                            className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            value={itemsDialog.floor_name}
                            readOnly
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Room</label>
                        <input
                            className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                            value={itemsDialog.room_name}
                            readOnly
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Date</label>
                          <input
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                              value={itemsDialog.merge_date}
                              readOnly
                          />
                      </div>
                      <div>
                          <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Time</label>
                          <input
                              className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                              value={itemsDialog.merge_time}
                              readOnly
                          />
                      </div>
                    </div>
                    {itemsDialog?.tables?.map((item,i)=>{
                      return (
                        <div>
                            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Table {i+1}</label>
                            <input
                                className="mt-2 flex h-9 w-full text-white rounded-md border border-gray-600 bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-cwhite3/70 focus-visible:outline-none cursor-not-allowed disabled:opacity-50 md:text-sm"
                                value={item.name}
                                readOnly
                            />
                        </div>
                      )
                    })}
                    {/* <div className="mt-4 flex gap-5 w-full">
                      <Button isLoading={loading} className="w-full bg-cyellow text-base font-medium" onClick={()=>{setFormType('update')}}>
                        Edit
                      </Button>
                    </div> */}
                </div> : 
                  <form onSubmit={form.handleSubmit(addFloorCardData)} className="flex flex-col gap-6">
                    <div onClick={()=>setRoomReset(true)}>
                      <FormInputField
                        name="floor_id"
                        style="bg-cblack3"
                        placeholder={"Select Floor"}
                        form={form}
                        type="select"
                        parseAs="string"
                        selectOption={floors?.filter(item => item.status !== "inactive")?.map(item => ({
                          label: item.name,
                          value: item.id,
                        }))}
                        label={"Floor"}
                        required={true}
                      />
                    </div>
                    <FormInputField
                      name="room_id"
                      style="bg-cblack3"
                      placeholder={"Select Room"}
                      form={form}
                      type="select"
                      parseAs="string"
                      selectOption={rooms?.filter(item => item.status !== "inactive")?.map(item => ({
                        label: item.name,
                        value: item.id,
                      }))}
                      label={"Room"}
                      required={true}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <FormInputField name="merge_date" style="bg-cblack3" placeholder={"Select Date"} form={form} type="date" dateFormat="yyyy-MM-dd" label={"Date"} autoSetToday={false} />
                      </div>
                      <div>
                        <FormInputField name="merge_time" style="bg-cblack3" placeholder={"Select Start Time"} form={form} type="time" label={"Start Time"} />
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {tableIds.map((tableId, index) => (
                        <div key={index} className="flex items-center gap-2 mb-3">
                          <FormInputField
                            name={`table_ids.${index}`}
                            style="bg-cblack3"
                            placeholder={`Table ${index + 1}`}
                            form={form}
                            type="select"
                            parseAs="string"
                            selectOption={tables
                              ?.filter(t => {
                                const previousSelected = tableIds.slice(0, index);
                                return !previousSelected.includes(t.id) || t.id === tableId;
                              })
                              .map(item => ({
                                label: item.name,
                                value: item.id,
                              }))}
                            label={`Table ${index + 1}`}
                            required
                            onValueChange={(val) => updateTable(val, index)}
                          />

                          {/* ❌ only from 3rd table */}
                          {index >= 2 && (
                            <X
                              size={18}
                              className="cursor-pointer text-red-500 mt-6"
                              onClick={() => removeTable(index)}
                            />
                          )}
                        </div>
                      ))}

                      {tableIds.length >= MIN_TABLE && tableIds.length < MAX_TABLE && (
                        <Button
                          type="button"
                          onClick={addTable}
                          className="w-fit bg-[#00d134] mt-2 text-sm font-medium"
                        >
                          Add More
                        </Button>
                      )}

                    </div>

                    <div className="mt-2 flex gap-5 w-full">
                      <Button
                        isLoading={loading}
                        className="w-full bg-[#ffbb00] text-base font-medium"
                      >
                        {formType === "update" ? "Update Merge" : "Confirm"}
                      </Button>
                    </div>
                  </form>
                }
              </Form>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default MergeTableModal;
